import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";

import { cx } from "@/lib/cx";

/**
 * A scrollable region that says so.
 *
 * This exists because of a specific bug in the card editor (UI-REVIEW P11): the
 * form always scrolled, and "Due date" and "Tags" were sliced clean through by
 * the sticky footer with nothing to mark the cut, so it read as a rendering
 * fault rather than as content continuing below. A four-pixel gradient is the
 * whole fix.
 *
 * The shadows are driven by measurement, not by a guess: they appear only when
 * there is genuinely something out of view in that direction, so a short body
 * gets no chrome at all. Both a scroll and a resize can change that answer —
 * a dialog whose content grows while open must gain the bottom shadow — so both
 * are observed.
 */

export type ScrollShadowProps = {
  children: ReactNode;
  /** Wrapper classes. */
  className?: string;
  /** Classes for the scrolling element itself — this is where padding goes. */
  contentClassName?: string;
  /**
   * The colour the gradient fades from. Must match the surface behind the
   * content, or the shadow reads as a grey band.
   *
   * Defaults to `--busee-overlay-panel`, the exact opaque surface used by the
   * two containers this appears inside: the dialog and the peek.
   */
  from?: string;
};

export function ScrollShadow({
  children,
  className,
  contentClassName,
  from = "var(--busee-overlay-panel)",
}: ScrollShadowProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ top: false, bottom: false });

  const measure = useCallback(() => {
    const node = scrollRef.current;
    if (!node) {
      return;
    }
    const { scrollTop, scrollHeight, clientHeight } = node;
    // A one-pixel tolerance: sub-pixel layout makes the bottom of a fully
    // scrolled region land at scrollHeight - clientHeight - 0.5 often enough
    // that an exact comparison leaves the shadow stuck on.
    const next = {
      top: scrollTop > 1,
      bottom: scrollTop + clientHeight < scrollHeight - 1,
    };
    setEdges((current) =>
      current.top === next.top && current.bottom === next.bottom ? current : next,
    );
  }, []);

  useEffect(() => {
    const node = scrollRef.current;
    if (!node) {
      return;
    }
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    // The content, not just the viewport — a body that grows taller while the
    // dialog is open changes the answer without resizing the scroller.
    for (const child of Array.from(node.children)) {
      observer.observe(child);
    }
    return () => observer.disconnect();
  }, [measure]);

  return (
    <div className={cx("relative flex min-h-0 flex-1 flex-col", className)}>
      <div
        ref={scrollRef}
        onScroll={measure}
        className={cx("min-h-0 flex-1 overflow-y-auto overscroll-contain", contentClassName)}
      >
        {children}
      </div>

      <Edge visible={edges.top} placement="top" from={from} />
      <Edge visible={edges.bottom} placement="bottom" from={from} />
    </div>
  );
}

function Edge({
  visible,
  placement,
  from,
}: {
  visible: boolean;
  placement: "top" | "bottom";
  from: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        "pointer-events-none absolute inset-x-0 h-4 dur-fast transition-opacity",
        placement === "top" ? "top-0" : "bottom-0",
        visible ? "opacity-100" : "opacity-0",
      )}
      style={{
        backgroundImage: `linear-gradient(to ${placement === "top" ? "bottom" : "top"}, ${from}, transparent)`,
      }}
    />
  );
}
